// Stealth-Agent FE — shared design primitives. Million-dollar-SaaS feel: restrained
// palette, refined typography, soft layered depth (NOT glassmorphism), and tasteful
// framer-motion micro-interactions. Isolated — imports nothing from TG's own code.
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';

// ── Motion presets ───────────────────────────────────────────────────────────
export const EASE = [0.22, 1, 0.36, 1]; // a calm, expensive-feeling ease-out

export const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};

// Section that animates its children in with a gentle stagger when scrolled into view.
export function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}

export function RevealGroup({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} className={className} variants={stagger} initial="hidden" animate={inView ? 'show' : 'hidden'}>
      {children}
    </motion.div>
  );
}

export const Item = ({ children, className = '' }) => (
  <motion.div variants={fadeUp} className={className}>{children}</motion.div>
);

// ── Card: the core surface. Soft, layered, hover-lift. ────────────────────────
export function Card({ children, className = '', hover = false, ...rest }) {
  return (
    <motion.div
      whileHover={hover ? { y: -3 } : undefined}
      transition={{ duration: 0.25, ease: EASE }}
      className={
        'rounded-2xl bg-white dark:bg-[#0f1115] ' +
        'border border-gray-200/80 dark:border-white/[0.06] ' +
        'shadow-[0_1px_2px_rgba(16,24,40,0.04),0_1px_3px_rgba(16,24,40,0.06)] ' +
        (hover ? 'hover:shadow-[0_8px_30px_rgba(16,24,40,0.08)] dark:hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)] ' : '') +
        'transition-shadow ' + className
      }
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ── CountUp: animated number that springs to its value (tabular, premium feel). ─
export function CountUp({ value, format = (n) => Math.round(n).toLocaleString(), className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 90, damping: 20, mass: 0.6 });
  const [text, setText] = useState(format(0));
  useEffect(() => { if (inView) mv.set(Number(value) || 0); }, [inView, value, mv]);
  useEffect(() => spring.on('change', (v) => setText(format(v))), [spring, format]);
  return <span ref={ref} className={'tabular-nums ' + className}>{text}</span>;
}

// ── KPI tile ──────────────────────────────────────────────────────────────────
export function Kpi({ icon: Icon, label, value, sub, accent = 'indigo', isNumber = false }) {
  const accents = {
    indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10',
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10',
    sky: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10',
    rose: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10',
  };
  return (
    <Item>
      <Card hover className="p-3.5 group min-h-[142px]">
        <div className="flex items-start justify-between">
          <div className={'w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105 ' + (accents[accent] || accents.indigo)}>
            {Icon && <Icon className="w-[18px] h-[18px]" />}
          </div>
        </div>
        <div className="mt-2.5">
          <div className="text-[26px] leading-none font-semibold tracking-tight text-gray-900 dark:text-white">
            {isNumber ? <CountUp value={value} /> : value}
          </div>
          <div className="text-[13px] text-gray-500 dark:text-gray-400 mt-1.5">{label}</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-2 leading-relaxed min-h-[32px]">
            {sub || <span className="opacity-0">placeholder</span>}
          </div>
        </div>
      </Card>
    </Item>
  );
}

// ── Section heading ─────────────────────────────────────────────────────────
export function SectionTitle({ icon: Icon, children, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-[15px] font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
        {Icon && <Icon className="w-[16px] h-[16px] text-gray-400" />} {children}
      </h3>
      {right}
    </div>
  );
}

// ── Page header: title + eyebrow + actions ──────────────────────────────────
export function PageHeader({ icon: Icon, title, subtitle, badge, actions }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="flex items-start justify-between flex-wrap gap-4"
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
            <Icon className="w-[22px] h-[22px] text-white" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900 dark:text-white">{title}</h1>
            {badge && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300 ring-1 ring-inset ring-violet-200/60 dark:ring-violet-500/20">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="text-[13.5px] text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </motion.div>
  );
}

// ── Skeleton shimmer (replaces spinners for a premium loading feel) ──────────
export function Skeleton({ className = '' }) {
  return (
    <div className={'relative overflow-hidden rounded-lg bg-gray-100 dark:bg-white/[0.04] ' + className}>
      <motion.div
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 dark:via-white/[0.06] to-transparent"
        animate={{ x: ['-100%', '200%'] }}
        transition={{ repeat: Infinity, duration: 1.4, ease: 'linear' }}
      />
    </div>
  );
}

// Animated thin bar (for active %), springs to width.
export function Bar({ pct, color = '#6366f1', track = 'rgba(99,102,241,0.12)', height = 6 }) {
  return (
    <div className="w-full rounded-full overflow-hidden" style={{ background: track, height }}>
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: EASE }}
        style={{ height, background: color, borderRadius: 999 }}
      />
    </div>
  );
}

// Refined palette — restrained, harmonious (indigo/violet/sky/emerald family).
export const CHART_PALETTE = ['#6366f1', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#14b8a6', '#a78bfa'];

// Live "online" pulse dot.
export function PulseDot({ on }) {
  if (!on) return <span className="inline-block w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />;
  return (
    <span className="relative inline-flex w-2 h-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
    </span>
  );
}

// ── Report PDF export ─────────────────────────────────────────────────────────
// Opens a clean, branded print window with the report HTML and triggers the browser's
// print dialog (→ "Save as PDF"). Text stays crisp + selectable + auto-paginated, with
// NO heavy rasterization (html2canvas) and no new dependency. `bodyHtml` is the already-
// rendered report HTML (from mdToHtml); `meta` adds the title/subtitle/generated stamp.
export function downloadReportPDF({ title, subtitle, bodyHtml, meta = {} }) {
  const safe = (s) => String(s || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const metaRows = Object.entries(meta)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `<span class="meta-item"><b>${safe(k)}:</b> ${safe(v)}</span>`)
    .join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${safe(title)}</title>
  <style>
    @page { margin: 16mm 14mm; }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2937; line-height: 1.65; font-size: 13px; margin: 0; }
    .doc { max-width: 760px; margin: 0 auto; }
    .head { border-bottom: 2px solid #6366f1; padding-bottom: 14px; margin-bottom: 22px; }
    .brand { display: flex; align-items: center; gap: 9px; }
    .logo { width: 26px; height: 26px; border-radius: 7px; background: linear-gradient(135deg,#6366f1,#7c3aed); }
    .brand-name { font-weight: 700; font-size: 13px; letter-spacing: -.01em; color: #111827; }
    h1.title { font-size: 21px; font-weight: 700; letter-spacing: -.02em; margin: 12px 0 3px; color: #0f172a; }
    .subtitle { color: #64748b; font-size: 12.5px; margin: 0; }
    .meta { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 11.5px; color: #64748b; }
    .meta b { color: #334155; font-weight: 600; }
    h2 { font-size: 15px; font-weight: 700; color: #111827; margin: 20px 0 8px; letter-spacing: -.01em; page-break-after: avoid; }
    h3 { font-size: 13.5px; font-weight: 700; color: #1f2937; margin: 14px 0 5px; page-break-after: avoid; }
    p { margin: 5px 0; }
    ul { margin: 6px 0; padding-left: 20px; }
    li { margin: 3px 0; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11.5px; page-break-inside: avoid; }
    th { text-align: left; padding: 7px 9px; background: #f1f5f9; color: #475569; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0; }
    td { padding: 7px 9px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
    tr:nth-child(even) td { background: #fafbfc; }
    td:first-child { color: #6366f1; font-weight: 500; white-space: nowrap; }
    code { background: #eef2ff; color: #4f46e5; padding: 1px 5px; border-radius: 4px; font-size: 11px; }
    .foot { margin-top: 26px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #94a3b8; text-align: center; }
  </style></head>
  <body><div class="doc">
    <div class="head">
      <div class="brand"><div class="logo"></div><div class="brand-name">TeamGrid · Stealth Intelligence</div></div>
      <h1 class="title">${safe(title)}</h1>
      ${subtitle ? `<p class="subtitle">${safe(subtitle)}</p>` : ''}
      ${metaRows ? `<div class="meta">${metaRows}</div>` : ''}
    </div>
    <div class="report-body">${bodyHtml || '<p>(no content)</p>'}</div>
    <div class="foot">Confidential · Generated by TeamGrid Stealth Intelligence</div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) { alert('Please allow pop-ups to download the report.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// A prominent, accurate online/offline pill — used consistently on both pages.
// `online` boolean + optional `lastSeen` (relative string) + optional `interval`.
export function StatusPill({ online, lastSeen, interval }) {
  return (
    <div className={
      'inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset ' +
      (online
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20'
        : 'bg-gray-100 text-gray-500 ring-gray-200 dark:bg-white/[0.04] dark:text-gray-400 dark:ring-white/10')
    }>
      <PulseDot on={online} />
      <span>{online ? 'Online' : 'Offline'}</span>
      {lastSeen && <span className="text-[11px] font-normal opacity-70">· {online ? 'live' : `seen ${lastSeen}`}</span>}
      {interval ? <span className="text-[11px] font-normal opacity-60">· {interval}s</span> : null}
    </div>
  );
}
